import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ChevronDown, X } from "lucide-react";

const ROLE_LABEL = {
  admin: "Admin", creator: "Creator", confirmer: "Confirmation", approver1: "1st Approver",
  approver2: "2nd Approver", auditor: "Audit", accounts: "Accounts",
};

export const CcPicker = ({ value, onChange }) => {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/users/cc-options").then(({ data }) => setUsers(data)).catch(() => {});
  }, []);

  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  const selected = users.filter((u) => value.includes(u.id));
  const shown = users.filter((u) => u.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="cc-picker">
      <Label className="text-xs uppercase tracking-wider text-slate-500">CC (view only)</Label>
      <Popover>
        <PopoverTrigger asChild>
          <button type="button" className="mt-1.5 w-full min-h-10 flex items-center justify-between gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm text-left" data-testid="cc-trigger">
            <span className={selected.length ? "" : "text-muted-foreground"}>
              {selected.length ? `${selected.length} user(s) selected` : "Select users to CC"}
            </span>
            <ChevronDown className="w-4 h-4 opacity-50 shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-2" align="start">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search users…" className="h-8 mb-2" data-testid="cc-search" />
          <div className="max-h-60 overflow-y-auto space-y-0.5">
            {shown.length === 0 && <p className="text-xs text-slate-400 px-2 py-3">No users found</p>}
            {shown.map((u) => (
              <label key={u.id} className="flex items-center gap-2.5 px-2 py-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-800 cursor-pointer" data-testid={`cc-option-${u.id}`}>
                <Checkbox checked={value.includes(u.id)} onCheckedChange={() => toggle(u.id)} />
                <span className="text-sm flex-1 truncate">{u.name}</span>
                <span className="text-[10px] uppercase tracking-wider text-slate-400">{ROLE_LABEL[u.role] || u.role}</span>
              </label>
            ))}
          </div>
        </PopoverContent>
      </Popover>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2" data-testid="cc-selected">
          {selected.map((u) => (
            <span key={u.id} className="inline-flex items-center gap-1 text-xs bg-primary/10 text-primary rounded-full pl-2.5 pr-1 py-0.5">
              {u.name}
              <button type="button" onClick={() => toggle(u.id)} className="rounded-full hover:bg-primary/20 p-0.5" data-testid={`cc-remove-${u.id}`}>
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
      <p className="text-xs text-slate-400 mt-1.5">CC users can view this voucher and its attachments once it is submitted.</p>
    </div>
  );
};
